import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl, FormGroup } from '@angular/forms';
import { GridDataResult, PageChangeEvent, RowReorderEvent } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, FilterDescriptor, SortDescriptor } from '@progress/kendo-data-query';
import { RtaService } from '../../shared/services/rta.service';
import { OrderDashboardItem, ReviewQueueItemDto } from '../../shared/interfaces/rta';
import { AdminService } from '../../shared/services/admin.service';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { insertMiddleIcon, pencilIcon, SVGIcon } from "@progress/kendo-svg-icons";
import { SignalRService } from '../../shared/services/signalr.service';
import { RTAApiClient } from '../../shared/services/rta-api.service';
import { GlobalNotificationService } from '../../shared/services/globalnotification.service';

declare var CURRENT_USER_ID: number;
declare var CURRENT_USER_LOGINID: string;
declare var CURRENT_USER_DISPLAYNAME: string;

interface TaskItem {
  Order_Id: number;
  Sequence: number;
  Name: string;
  Status: 'C' | 'Q' | 'Pr' | 'Pe'; // Completed, Queued, In Progress, Pending
}

interface ParentTask extends TaskItem {
  childTasks: string; // JSON string of TaskItem[]
}

interface DesignRequest {
  ID: number;
  SubPSL_Name: string;
  ProductLineName: string;
  TypeName: string;
  Region: string;
  Geography_Name: string | null;
  Application_Name: string | null;
  SeriesName: string;
  BitSize: number | null;
  BitType: string | null;
  Description: string;
  TotalCostSavings: number | null;
  SubmittedBy: string;
  Status_ID: number;
  Status_Name: string;
  RequiredDate: string;
  SubmittedDate: string;
  EngAssignedDate: string | null;
  EngCompletedDate: string | null;
  ApprovedDate: string | null;
  CompletedDate: string | null;
  OnHold: string;
  Canceled: string;
  Sequence: number;
  PriorityStatus: string;
  PriorityId: number | null;
  TaskData?: string; // JSON string of ParentTask[]
  Size?: string | null; // For display purposes, e.g. "4 3/4" instead of "4.75"
  Type?: string | null; // For display purposes, e.g. "MM54", "XR545", etc.
  Level?: number | null;
  ReferenceNumber?: number | null;
  MaterialNumber?: number | null;
  ServicesNumber?: number | null;
  DesignedBy_DisplayName?: string | null;
  IsComplete?: boolean;
}

interface Scorecard {
  label: string;
  value: number;
  change: number;
  changeLabel: string;
  icon: string;
  color: string;
}

interface StatusCount {
  status: string;
  statusKey: string;
  count: number;
}

interface GroupAnalyticsRow {
  name: string;
  completed: number;
  inProgress: number;
  avgTurnaround: number;
  activeRequests: number;
}

interface ReviewQueueItem {
  id: number;
  orderTaskId: number;
  title: string;
  requestedBy: string;
  productLine: string;
  type: string;
  processName: string;
  dueDate: string;
  priority: 'High' | 'Medium' | 'Low';
  icon: string;
  isOverdue: boolean;
  isApproval: boolean;
}

interface UpcomingTask {
  id: number;
  title: string;
  status: string;
  dueDate: string;
  assignee: string;
  priority: 'High' | 'Medium' | 'Low';
  isOverdue: boolean;
}

interface ViewOptions {
  showScorecards: boolean;
  showAnalytics: boolean;
  showActionCenter: boolean;
  showStatusOverview: boolean;
  showFilters: boolean;
  showActivity: boolean;
}

interface PriorityOption {
  id: number;
  name: string;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
  standalone: false
})
export class DashboardComponent implements OnInit, OnDestroy {
  public dragIcon: SVGIcon = insertMiddleIcon;
  public pencilIcon: SVGIcon = pencilIcon;

  private readonly viewStorageKey = 'dashboard.viewOptions';
  private readonly statusIdMap: Record<number, string> = {
    24: 'Released',
    18: 'Completed',
    15: 'In Progress',
    12: 'Pending',
    10: 'On Hold'
  };

  filterForm: FormGroup;

  // Current user info
  currentUser = {
    id: CURRENT_USER_ID,
    name: CURRENT_USER_DISPLAYNAME,
    loginId: CURRENT_USER_LOGINID,
    role: 'Design Engineer'
  };
  isAdmin = false;

  // Filter options (populated from API)
  statusOptions: string[] = [];
  productLineOptions: string[] = [];
  typeOptions: string[] = [];
  regionOptions: string[] = [];
  bitSeriesOptions: string[] = [];
  assigneeOptions = ['Me', 'Unassigned'];
  timeRanges = ['Last 30 Days', 'Last 90 Days', 'This Year', 'All Time'];
  selectedTimeRange = 'Last 30 Days';

  // Grid settings
  pageSize = 10;
  skip = 0;
  sort: SortDescriptor[] = [{ field: 'SubmittedDate', dir: 'asc' }];
  gridFilter: CompositeFilterDescriptor = { logic: 'and', filters: [] };
  showMyRequests = false;
  showCompleted = false;
  activeStatusFilter: string | null = null;

  // Data
  allRequests: DesignRequest[] = [];
  filteredRequests: DesignRequest[] = [];
  gridData: GridDataResult = { data: [], total: 0 };
  statusCounts: StatusCount[] = [];
  totalCount = 0;
  reviewQueue: ReviewQueueItem[] = [];
  filteredReviewQueue: ReviewQueueItem[] = [];
  reviewSearchTerm = '';
  upcomingTasks: UpcomingTask[] = [];
  priorityOptions: PriorityOption[] = [];
  canReorderRows = false;

  // Priority popover state
  priorityPopoverOpen: number | null = null; // Stores the ID of the row with open popover
  priorityPopoverAnchor: HTMLElement | null = null;

  // New Request Modal
  showNewRequestModal = false;

  // Dashboard tab toggle (eRTA grid vs iRTA change list)
  dashboardTab: 'eRTA' | 'iRTA' = 'eRTA';

  // Grid search
  gridSearchTerm = '';

  viewOptions: ViewOptions = {
    showScorecards: false,
    showAnalytics: false,
    showActionCenter: false,
    showStatusOverview: false,
    showFilters: false,
    showActivity: false
  };
  viewMode: 'minimal' | 'full' | 'custom' = 'minimal';

  // Scorecards
  scorecards: Scorecard[] = [];

  // Chart data
  chartCategories: string[] = [];
  userPerformanceData: number[] = [];
  teamPerformanceData: number[] = [];

  statusChartData = [
    { status: 'Released', value: 0, color: '#1976d2' },
    { status: 'Completed', value: 0, color: '#388e3c' },
    { status: 'In Progress', value: 0, color: '#f57c00' },
    { status: 'Pending', value: 0, color: '#7b1fa2' },
    { status: 'On Hold', value: 0, color: '#d32f2f' },
    { status: 'Canceled', value: 0, color: '#546e7a' }
  ];

  avgTurnaroundDays = {
    user: 0,
    team: 0
  };

  groupAnalytics: GroupAnalyticsRow[] = [];

  // Recent Activity
  recentActivity = [
    { type: 'completed', icon: 'k-i-check', text: 'Completed request #900001', time: '2 hours ago' },
    { type: 'assigned', icon: 'k-i-user', text: 'Assigned to request #900008', time: '5 hours ago' },
    { type: 'submitted', icon: 'k-i-file-add', text: 'Submitted new request #900012', time: 'Yesterday' },
    { type: 'commented', icon: 'k-i-comment', text: 'Commented on #900005', time: '2 days ago' }
  ];

  // Reject dialog
  showRejectDialog = false;
  rejectingReview: ReviewQueueItem | null = null;
  rejectionReason = '';
  isProcessingReview = false;

  constructor(
    private router: Router,
    private rtaService: RtaService,
    private rtaApiClient: RTAApiClient,
    private adminService: AdminService,
    private signalRService: SignalRService,
    private notification: GlobalNotificationService
  ) {
    this.filterForm = new FormGroup({
      search: new FormControl(''),
      status: new FormControl([]),
      productLine: new FormControl([]),
      type: new FormControl(null),
      region: new FormControl(null),
      bitSeries: new FormControl([]),
      dateFrom: new FormControl(null),
      dateTo: new FormControl(null),
      assignedTo: new FormControl(null)
    });
  }

  ngOnInit(): void {
    this.isAdmin = this.currentUser.role.toLowerCase().includes('admin');
    this.loadUserPermissions();
    this.loadViewPreferences();
    this.loadPriorities();
    this.loadFilterOptions();
    this.fetchOrders(true);
    this.loadAnalytics();
    this.initializeSignalR();
  }

  /**
   * Initialize SignalR connection and subscribe to real-time events
   */
  private initializeSignalR(): void {
    // Start SignalR connection
    this.signalRService.startConnection().then(() => {
      console.log('Dashboard: SignalR connected');

      // Subscribe to task picked up events
      this.signalRService.onTaskPickedUp.subscribe(event => {
        if (event) {
          console.log('✅ Dashboard: Task picked up event received:', event);
          this.handleTaskPickedUp(event.taskId, event.userName, event.status);
        }
      });

      // Subscribe to task status changed events
      this.signalRService.onTaskStatusChanged.subscribe(event => {
        if (event) {
          console.log('✅ Dashboard: Task status changed event received:', event);
          this.handleTaskStatusChanged(event.taskId, event.oldStatus, event.newStatus, event.userName);
        }
      });

      // Subscribe to task updated events
      this.signalRService.onTaskUpdated.subscribe(event => {
        if (event) {
          console.log('✅ Dashboard: Task updated event received:', event);
          this.handleTaskUpdated(event.taskId, event.taskData);
        }
      });
    }).catch(err => {
      console.error('❌ Dashboard: SignalR connection failed', err);
    });
  }

  /**
   * Handle task picked up event
   */
  private handleTaskPickedUp(taskId: number, userName: string, status: string): void {
    // Find and update the order in the grid
    const orderIndex = this.allRequests.findIndex(r => r.ID === taskId);
    if (orderIndex !== -1) {
      this.allRequests[orderIndex].Status_Name = status;
      this.allRequests[orderIndex].SubmittedBy = userName;

      // Refresh the grid data
      this.filteredRequests = [...this.allRequests];
      this.calculateStatusCounts();
      this.loadGridData();

      // Show notification
      console.log(`${userName} picked up task #${taskId}`);
    }
  }

  /**
   * Handle task status changed event
   */
  private handleTaskStatusChanged(taskId: number, oldStatus: string, newStatus: string, userName: string): void {
    // Find and update the order in the grid
    const orderIndex = this.allRequests.findIndex(r => r.ID === taskId);
    if (orderIndex !== -1) {
      this.allRequests[orderIndex].Status_Name = newStatus;

      // Update completion date if completed
      if (newStatus === 'Completed') {
        this.allRequests[orderIndex].CompletedDate = new Date().toISOString();
      }

      // Refresh the grid data
      this.filteredRequests = [...this.allRequests];
      this.calculateStatusCounts();
      this.loadGridData();

      // Show notification
      console.log(`${userName} changed task #${taskId} from ${oldStatus} to ${newStatus}`);
    }
  }

  /**
   * Handle task updated event
   */
  private handleTaskUpdated(taskId: number, taskData: any): void {
    // Find and update the order in the grid
    const orderIndex = this.allRequests.findIndex(r => r.ID === taskId);
    if (orderIndex !== -1) {
      // Update status based on task data
      if (taskData.status) {
        this.allRequests[orderIndex].Status_Name = taskData.status;
      }

      // Refresh the grid data
      this.filteredRequests = [...this.allRequests];
      this.calculateStatusCounts();
      this.loadGridData();

      // Show notification
      console.log(`Task #${taskId} updated`);
    }
  }

  fetchOrders(resetSkip: boolean): void {
    if (resetSkip) {
      this.skip = 0;
    }

    const filters = this.filterForm.value;
    const assignedTo = filters.assignedTo === 'Me'
      ? this.currentUser.name
      : filters.assignedTo === 'Unassigned'
        ? '__unassigned__'
        : filters.assignedTo;
    const userFilter = this.showMyRequests ? this.currentUser.name : null;

    const activeSort = this.sort?.length ? this.sort[0] : null;

    this.rtaService.getOrders({
      skip: this.skip,
      take: this.pageSize,
      sortField: activeSort?.field,
      sortDir: activeSort?.dir,
      search: filters.search,
      status: filters.status,
      productLine: filters.productLine,
      type: filters.type && filters.type !== 'All Types' ? filters.type : null,
      series: filters.bitSeries,
      assignedTo: assignedTo && assignedTo !== 'Anyone' ? assignedTo : null,
      userFilter,
      region: filters.region && filters.region !== 'All Regions' ? filters.region : null,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      filterId: this.getColumnFilter('ID')?.value ?? null,
      filterIdOp: this.getColumnFilter('ID')?.operator ?? null,
      filterDescription: this.getColumnFilter('Description')?.value ?? null,
      filterDescriptionOp: this.getColumnFilter('Description')?.operator ?? null,
      filterSize: this.getColumnFilter('Size')?.value ?? null,
      filterSizeOp: this.getColumnFilter('Size')?.operator ?? null,
      filterType: this.getColumnFilter('Type')?.value ?? null,
      filterTypeOp: this.getColumnFilter('Type')?.operator ?? null,
      filterLevel: this.getColumnFilter('Level')?.value ?? null,
      filterLevelOp: this.getColumnFilter('Level')?.operator ?? null,
      filterReferenceNumber: this.getColumnFilter('ReferenceNumber')?.value ?? null,
      filterReferenceNumberOp: this.getColumnFilter('ReferenceNumber')?.operator ?? null,
      filterMaterialNumber: this.getColumnFilter('MaterialNumber')?.value ?? null,
      filterMaterialNumberOp: this.getColumnFilter('MaterialNumber')?.operator ?? null,
      filterServicesNumber: this.getColumnFilter('ServicesNumber')?.value ?? null,
      filterServicesNumberOp: this.getColumnFilter('ServicesNumber')?.operator ?? null,
      filterSubmittedBy: this.getColumnFilter('SubmittedBy')?.value ?? null,
      filterSubmittedByOp: this.getColumnFilter('SubmittedBy')?.operator ?? null,
      filterIsComplete: this.getIsCompleteFilter()
    }).subscribe({
      next: (result) => {
        this.allRequests = result.items.map(order => this.mapOrderToDesignRequest(order));
        this.filteredRequests = [...this.allRequests];
        this.totalCount = result.total;
        this.calculateStatusCounts();
        this.loadGridData();
        this.buildActionCenter();
      },
      error: (error) => {
        console.error('Failed to load orders', error);
        this.allRequests = [];
        this.filteredRequests = [];
        this.totalCount = 0;
        this.calculateStatusCounts();
        this.loadGridData();
      }
    });
  }

  private mapOrderToDesignRequest(order: OrderDashboardItem): DesignRequest {
    const payload = order as unknown as Record<string, any>;
    const id = payload['Id'] ?? payload['id'] ?? 0;
    const subPslName = payload['SubPslName'] ?? payload['subPslName'] ?? '';
    const productLineName = payload['ProductLineName'] ?? payload['productLineName'] ?? '';
    const typeName = payload['TypeName'] ?? payload['typeName'] ?? '';
    const region = payload['NwaName'] ?? payload['nwaName'] ?? '';
    const seriesName = payload['SeriesName'] ?? payload['seriesName'] ?? '';
    const notes = payload['NotesToDesignSupport'] ?? payload['notesToDesignSupport'] ?? '';
    const submittedBy = payload['SubmittedBy'] ?? payload['submittedBy'] ?? '';
    const designedBy = payload['DesignedBy'] ?? payload['designedBy'] ?? '';
    const activeAssignees = payload['ActiveAssignees'] ?? payload['activeAssignees'] ?? '';
    const designedByDisplay = activeAssignees || designedBy ? String(activeAssignees || designedBy) : null;
    const statusId = payload['StatusId'] ?? payload['statusId'] ?? 0;
    // Use status_Name from API if available, otherwise fall back to getStatusName()
    const statusName = payload['Status_Name'] ?? payload['status_Name'] ?? null;
    const requiredDateRaw = payload['RequiredDate'] ?? payload['requiredDate'] ?? '';
    const onHold = payload['OnHold'] ?? payload['onHold'] ?? false;
    const cancelled = payload['Cancelled'] ?? payload['cancelled'] ?? false;
    const sequence = payload['Sequence'] ?? payload['sequence'] ?? 0;
    const priorityStatus = payload['PriorityStatus'] ?? payload['priorityStatus'] ?? '';
    const priorityId = payload['Priority'] ?? payload['priority'] ?? null;
    const taskData = payload['TaskData'] ?? payload['taskData'] ?? undefined;
    const size = payload['Size'] ?? payload['size'] ?? null;
    const type = payload['Type'] ?? payload['type'] ?? null;
    const level = payload['Level'] ?? payload['level'] ?? null;
    const referenceNumber = payload['ReferenceNumber'] ?? payload['referenceNumber'] ?? null;
    const materialNumber = payload['MaterialNumber'] ?? payload['materialNumber'] ?? null;
    const servicesNumber = payload['ServicesNumber'] ?? payload['servicesNumber'] ?? null;
    const totalSavings = payload['TotalSavings'] ?? payload['totalSavings'] ?? null;
    const submittedDateRaw = payload['SubmittedDate'] ?? payload['submittedDate'] ?? null;
    const isComplete = payload['IsComplete'] ?? payload['isComplete'] ?? false;

    return {
      ID: id,
      SubPSL_Name: subPslName,
      ProductLineName: productLineName,
      TypeName: typeName,
      Region: region,
      Geography_Name: null,
      Application_Name: null,
      SeriesName: seriesName,
      BitSize: null,
      BitType: null,
      Description: notes,
      TotalCostSavings: totalSavings,
      SubmittedBy: submittedBy ? String(submittedBy) : '',
      Status_ID: statusId,
      Status_Name: statusName ?? this.getStatusName(payload as OrderDashboardItem),
      RequiredDate: requiredDateRaw ? new Date(requiredDateRaw).toISOString() : '',
      SubmittedDate: submittedDateRaw ? new Date(submittedDateRaw).toISOString() : '',
      EngAssignedDate: null,
      EngCompletedDate: null,
      ApprovedDate: null,
      CompletedDate: null,
      OnHold: onHold ? '1' : '0',
      Canceled: cancelled ? '1' : '0',
      Sequence: sequence ?? 0,
      PriorityStatus: priorityStatus ?? '',
      PriorityId: priorityId ?? null,
      TaskData: taskData,
      Size: size ? String(size) : null,
      Type: type ? String(type) : null,
      Level: level,
      ReferenceNumber: referenceNumber,
      MaterialNumber: materialNumber,
      ServicesNumber: servicesNumber,
      DesignedBy_DisplayName: designedByDisplay,
      IsComplete: isComplete
    };
  }

  private getStatusName(order: OrderDashboardItem): string {
    const payload = order as unknown as Record<string, any>;
    const cancelled = payload['Cancelled'] ?? payload['cancelled'] ?? false;
    const onHold = payload['OnHold'] ?? payload['onHold'] ?? false;
    const statusId = payload['StatusId'] ?? payload['statusId'] ?? 0;

    if (cancelled) {
      return 'Canceled';
    }
    if (onHold) {
      return 'On Hold';
    }
    if (statusId && this.statusIdMap[statusId]) {
      return this.statusIdMap[statusId];
    }
    if (statusId) {
      return `Status ${statusId}`;
    }
    return 'Pending';
  }

  calculateStatusCounts(): void {
    const counts: Record<string, number> = {};

    this.allRequests.forEach(request => {
      const status = request.Status_Name;
      counts[status] = (counts[status] || 0) + 1;
    });

    this.statusCounts = [
      { status: 'Released', statusKey: 'released', count: counts['Released'] || 0 },
      { status: 'Completed', statusKey: 'completed', count: counts['Completed'] || 0 },
      { status: 'In Progress', statusKey: 'in-progress', count: counts['In Progress'] || 0 },
      { status: 'Pending', statusKey: 'pending', count: counts['Pending'] || 0 },
      { status: 'On Hold', statusKey: 'on-hold', count: counts['On Hold'] || 0 },
      { status: 'Canceled', statusKey: 'canceled', count: counts['Canceled'] || 0 }
    ];

    // Update chart data
    this.statusChartData = this.statusCounts.map(s => ({
      status: s.status,
      value: s.count,
      color: this.getStatusColor(s.statusKey)
    }));
  }

  getStatusColor(statusKey: string): string {
    const colors: Record<string, string> = {
      'released': '#1976d2',
      'completed': '#388e3c',
      'in-progress': '#f57c00',
      'pending': '#7b1fa2',
      'on-hold': '#d32f2f',
      'canceled': '#546e7a'
    };
    return colors[statusKey] || '#546e7a';
  }

  getStatusKey(statusName: string): string {
    return statusName.toLowerCase().replace(/\s+/g, '-');
  }

  // ── Meta-tag coloring ──────────────────────────────────────────────────────
  // Each distinct value gets a stable color from this palette, assigned on
  // first encounter and reused every time the same value appears.
  private readonly metaTagPalette: { bg: string; color: string }[] = [
    { bg: '#fff3d6', color: '#b45309' }, // amber
    { bg: '#e6efff', color: '#1d4ed8' }, // blue
    { bg: '#e6f7f5', color: '#0f766e' }, // teal
    { bg: '#fce7f3', color: '#9d174d' }, // pink
    { bg: '#f3e8ff', color: '#6d28d9' }, // purple
    { bg: '#dcfce7', color: '#166534' }, // green
    { bg: '#ffedd5', color: '#c2410c' }, // orange
    { bg: '#e0f2fe', color: '#0369a1' }, // sky
    { bg: '#fef9c3', color: '#854d0e' }, // yellow
    { bg: '#f1f5f9', color: '#475569' }, // slate
  ];
  private metaTagColorMap = new Map<string, { bg: string; color: string }>();

  getMetaTagStyle(value: string): { [key: string]: string } {
    if (!value) return {};
    if (!this.metaTagColorMap.has(value)) {
      const idx = this.metaTagColorMap.size % this.metaTagPalette.length;
      this.metaTagColorMap.set(value, this.metaTagPalette[idx]);
    }
    const { bg, color } = this.metaTagColorMap.get(value)!;
    return { background: bg, color };
  }
  // ──────────────────────────────────────────────────────────────────────────

  getPriorityKey(priorityStatus: string): string {
    if (!priorityStatus) {
      return 'none';
    }
    return priorityStatus.toLowerCase().replace(/\s+/g, '-');
  }

  filterByStatus(status: string): void {
    if (this.activeStatusFilter === status) {
      this.activeStatusFilter = null;
      this.filterForm.patchValue({ status: [] });
    } else {
      this.activeStatusFilter = status;
      this.filterForm.patchValue({ status: [status] });
    }
    this.applyFilters();
  }

  onFilterChange(): void {
    this.activeStatusFilter = null;
    this.applyFilters();
  }

  hasActiveFilters(): boolean {
    const f = this.filterForm.value;
    return f.search || f.status?.length || f.productLine?.length ||
      f.type || f.region || f.bitSeries?.length ||
      f.dateFrom || f.dateTo || f.assignedTo;
  }

  applyFilters(): void {
    this.fetchOrders(true);
  }

  loadPriorities(): void {
    this.rtaService.getPriorities().subscribe({
      next: (priorities) => {
        this.priorityOptions = priorities ?? [];
      },
      error: (error) => {
        console.error('Failed to load priorities', error);
        this.priorityOptions = [];
      }
    });
  }

  loadFilterOptions(): void {
    this.rtaService.getDashboardFilterOptions().subscribe({
      next: (options) => {
        this.statusOptions = options.statuses ?? [];
        this.typeOptions = options.types ?? [];
        this.productLineOptions = options.productLines ?? [];
        this.bitSeriesOptions = options.series ?? [];
        this.regionOptions = options.regions ?? [];
      },
      error: (error) => {
        console.error('Failed to load dashboard filter options', error);
        // Fall back to known status values so the status filter still works
        this.statusOptions = ['Released', 'Completed', 'In Progress', 'Pending', 'On Hold', 'Canceled'];
      }
    });
  }

  clearFilters(): void {
    this.filterForm.reset({
      search: '',
      status: [],
      productLine: [],
      type: null,
      region: null,
      bitSeries: [],
      dateFrom: null,
      dateTo: null,
      assignedTo: null
    });
    this.activeStatusFilter = null;
    this.showMyRequests = false;
    this.applyFilters();
  }

  loadGridData(): void {
    this.gridData = {
      data: this.filteredRequests,
      total: this.totalCount
    };
  }

  loadAnalytics(): void {
    this.rtaService.getDashboardAnalytics(this.selectedTimeRange).subscribe({
      next: (analytics) => {
        // Performance chart
        this.chartCategories = analytics.chartCategories ?? [];
        this.userPerformanceData = analytics.userPerformanceData ?? [];
        this.teamPerformanceData = analytics.teamPerformanceData ?? [];

        // Status breakdown donut chart
        if (analytics.statusChartData?.length) {
          this.statusChartData = analytics.statusChartData.map(item => ({
            status: item.status,
            value: item.value,
            color: this.getStatusColor(item.status.toLowerCase().replace(/\s+/g, '-'))
          }));
        }

        // Turnaround times
        this.avgTurnaroundDays = {
          user: analytics.avgTurnaroundUser ?? 0,
          team: analytics.avgTurnaroundTeam ?? 0
        };

        // Group analytics (admin grid)
        this.groupAnalytics = (analytics.groupAnalytics ?? []).map(g => ({
          name: g.name,
          completed: g.completed,
          inProgress: g.inProgress,
          avgTurnaround: g.avgTurnaround,
          activeRequests: g.activeRequests
        }));

        // Scorecards
        const iconMap: Record<string, string> = {
          'Total Submitted': 'k-i-file-add',
          'Completed': 'k-i-check-circle',
          'In Progress': 'k-i-clock'
        };
        const colorMap: Record<string, string> = {
          'Total Submitted': 'blue',
          'Completed': 'green',
          'In Progress': 'orange'
        };
        this.scorecards = (analytics.scorecards ?? []).map(s => ({
          label: s.label,
          value: s.value,
          change: s.change,
          changeLabel: s.changeLabel,
          icon: iconMap[s.label] || 'k-i-info',
          color: colorMap[s.label] || 'blue'
        }));
      },
      error: (error) => {
        console.error('Failed to load analytics', error);
      }
    });
  }

  onPageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.pageSize = event.take;
    this.fetchOrders(false);
  }

  onSortChange(sort: SortDescriptor[]): void {
    this.sort = sort;
    this.fetchOrders(false);
  }

  onGridFilterChange(filter: CompositeFilterDescriptor): void {
    this.gridFilter = filter;
    this.fetchOrders(true);
  }

  private getColumnFilter(field: string): { value: string; operator: string } | null {
    if (!this.gridFilter?.filters?.length) return null;
    for (const f of this.gridFilter.filters) {
      const composite = f as CompositeFilterDescriptor;
      if (composite.filters) {
        for (const inner of composite.filters) {
          const fd = inner as FilterDescriptor;
          if (fd.field === field && fd.value != null && fd.value !== '') {
            return { value: fd.value.toString(), operator: (fd.operator as string) || 'contains' };
          }
        }
      }
      const fd = f as FilterDescriptor;
      if (fd.field === field && fd.value != null && fd.value !== '') {
        return { value: fd.value.toString(), operator: (fd.operator as string) || 'contains' };
      }
    }
    return null;
  }

  private getIsCompleteFilter(): boolean | null {
    // Check if user has set a grid column filter on IsComplete
    const colFilter = this.getColumnFilter('IsComplete');
    if (colFilter !== null) {
      return colFilter.value === 'true' || colFilter.value === '1';
    }
    // Default: use the showCompleted toggle (false = hide completed, null = show all)
    return this.showCompleted ? null : false;
  }

  toggleShowCompleted(selected: boolean): void {
    this.showCompleted = selected;
    this.skip = 0;
    this.fetchOrders(true);
  }

  onTimeRangeChange(timeRange: string): void {
    this.selectedTimeRange = timeRange;
    this.loadAnalytics();
  }

  toggleMyRequests(selected: boolean): void {
    this.showMyRequests = selected;
    this.applyFilters();
  }

  isOverdue(request: DesignRequest): boolean {
    if (request.Status_Name === 'Completed' || request.Status_Name === 'Released' || request.Status_Name === 'Canceled') {
      return false;
    }
    const required = new Date(request.RequiredDate);
    return required < new Date();
  }

  loadReviewQueue(): void {
    this.rtaService.getReviewQueue().subscribe({
      next: (items) => {
        this.reviewQueue = items.map(item => ({
          id: item.id,
          orderTaskId: item.orderTaskId,
          title: item.description || item.notesToDesignSupport || '',
          requestedBy: item.submittedByName || '',
          productLine: item.productLineName || '',
          type: item.subPslName || '',
          processName: item.processName || '',
          dueDate: item.requiredDate || '',
          priority: this.getPriority(item.requiredDate || ''),
          icon: item.isApproval ? 'k-i-checkmark-circle' : 'k-i-clipboard',
          isOverdue: item.requiredDate ? new Date(item.requiredDate) < new Date() : false,
          isApproval: item.isApproval
        }));
        this.filterReviewQueue();
      },
      error: (err) => {
        console.error('Failed to load review queue', err);
        this.reviewQueue = [];
        this.filteredReviewQueue = [];
      }
    });
  }

  onReviewSearch(term: string): void {
    this.reviewSearchTerm = term || '';
    this.filterReviewQueue();
  }

  filterReviewQueue(): void {
    if (!this.reviewSearchTerm.trim()) {
      this.filteredReviewQueue = [...this.reviewQueue];
      return;
    }
    const term = this.reviewSearchTerm.toLowerCase();
    this.filteredReviewQueue = this.reviewQueue.filter(r =>
      r.title.toLowerCase().includes(term) ||
      r.requestedBy.toLowerCase().includes(term) ||
      r.productLine.toLowerCase().includes(term) ||
      r.type.toLowerCase().includes(term) ||
      r.processName.toLowerCase().includes(term) ||
      String(r.id).includes(term)
    );
  }

  buildActionCenter(): void {
    this.loadReviewQueue();

    this.upcomingTasks = this.allRequests
      .filter(request => !['Completed', 'Released', 'Canceled'].includes(request.Status_Name))
      .sort((a, b) => new Date(a.RequiredDate).getTime() - new Date(b.RequiredDate).getTime())
      .slice(0, 6)
      .map(request => ({
        id: request.ID,
        title: request.Description,
        status: request.Status_Name,
        dueDate: request.RequiredDate,
        assignee: request.SubmittedBy || 'Unassigned',
        priority: this.getPriority(request.RequiredDate),
        isOverdue: this.isOverdue(request)
      }));
  }

  getPriority(requiredDate: string): 'High' | 'Medium' | 'Low' {
    const now = new Date();
    const due = new Date(requiredDate);
    const diffDays = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays <= 2) {
      return 'High';
    }
    if (diffDays <= 7) {
      return 'Medium';
    }
    return 'Low';
  }

  updateRequestStatus(id: number, status: string): void {
    const request = this.allRequests.find(item => item.ID === id);
    if (request) {
      request.Status_Name = status;
    }
    this.calculateStatusCounts();
    this.applyFilters();
    this.buildActionCenter();
  }

  updatePriority(request: DesignRequest, priorityId: number): void {
    if (!priorityId || request.PriorityId === priorityId) {
      return;
    }
    const payload = {
      priorityId,
      sequence: request.Sequence ?? 0
    };

    this.rtaService.updateOrderPriority(request.ID, payload).subscribe({
      next: () => {
        const option = this.priorityOptions.find(item => item.id === priorityId);
        request.PriorityId = priorityId;
        request.PriorityStatus = option?.name ?? request.PriorityStatus;
        // Close the popover after successful update
        this.closePriorityPopover();
      },
      error: (error) => {
        console.error('Failed to update priority', error);
      }
    });
  }

  /**
   * Toggle the priority popover for a specific row
   */
  togglePriorityPopover(request: DesignRequest, event: MouseEvent): void {
    event.stopPropagation();

    if (this.priorityPopoverOpen === request.ID) {
      // Close if already open for this row
      this.closePriorityPopover();
    } else {
      // Open for this row
      this.priorityPopoverOpen = request.ID;
      this.priorityPopoverAnchor = event.target as HTMLElement;
    }
  }

  /**
   * Close the priority popover
   */
  closePriorityPopover(): void {
    this.priorityPopoverOpen = null;
    this.priorityPopoverAnchor = null;
  }

  /**
   * Check if popover is open for a specific row
   */
  isPriorityPopoverOpen(requestId: number): boolean {
    return this.priorityPopoverOpen === requestId;
  }

  private loadUserPermissions(): void {
    const setPermissions = () => {
      this.canReorderRows = this.adminService.CheckPermission('RTA_SET_PRIORITIES');
    };

    if (this.adminService.RetrieveUserPermissionsRetrieved) {
      setPermissions();
    } else {
      this.adminService.RetrievePermissionsCompleted.subscribe(() => setPermissions());
    }
  }

  onRowReorder(event: RowReorderEvent): void {
    if (!this.canReorderRows) {
      return;
    }

    if (event.dropPosition === 'forbidden' || !event.dropTargetRow || event.draggedRows.length === 0) {
      return;
    }

    const dragRow = event.draggedRows[0];
    const fromIndex = dragRow.rowIndex;
    let toIndex = event.dropTargetRow.rowIndex;
    if (event.dropPosition === 'after') {
      toIndex += 1;
    }

    const data = [...this.filteredRequests];
    const [moved] = data.splice(fromIndex, 1);
    if (toIndex > fromIndex) {
      toIndex -= 1;
    }
    data.splice(toIndex, 0, moved);

    data.forEach((item, index) => {
      item.Sequence = this.skip + index + 1;
    });

    this.filteredRequests = data;
    this.loadGridData();
    this.persistSequenceUpdates(data);
  }

  private persistSequenceUpdates(items: DesignRequest[]): void {
    const updates = items
      .filter(item => item.PriorityId)
      .map(item =>
        this.rtaService.updateOrderPriority(item.ID, {
          priorityId: item.PriorityId as number,
          sequence: item.Sequence ?? 0
        }).pipe(
          catchError(error => {
            console.error('Failed to update sequence', error);
            return of(null);
          })
        )
      );

    if (updates.length === 0) {
      return;
    }

    forkJoin(updates).subscribe();
  }

  toggleViewOption(option: keyof ViewOptions, value: boolean): void {
    this.viewOptions = {
      ...this.viewOptions,
      [option]: value
    };
    this.updateViewModeFromOptions();
    this.saveViewPreferences();
  }

  showMinimalView(): void {
    this.viewOptions = {
      showScorecards: false,
      showAnalytics: false,
      showActionCenter: false,
      showStatusOverview: false,
      showFilters: false,
      showActivity: false
    };
    this.viewMode = 'minimal';
    this.saveViewPreferences();
  }

  showAllSections(): void {
    this.viewOptions = {
      showScorecards: true,
      showAnalytics: true,
      showActionCenter: true,
      showStatusOverview: true,
      showFilters: true,
      showActivity: true
    };
    this.viewMode = 'full';
    this.saveViewPreferences();
  }

  private updateViewModeFromOptions(): void {
    const values = Object.values(this.viewOptions);
    const allEnabled = values.every(value => value);
    const allDisabled = values.every(value => !value);

    if (allEnabled) {
      this.viewMode = 'full';
      return;
    }
    if (allDisabled) {
      this.viewMode = 'minimal';
      return;
    }
    this.viewMode = 'custom';
  }

  private loadViewPreferences(): void {
    try {
      const stored = localStorage.getItem(this.viewStorageKey);
      if (!stored) {
        this.updateViewModeFromOptions();
        return;
      }
      const parsed = JSON.parse(stored) as Partial<ViewOptions>;
      this.viewOptions = {
        ...this.viewOptions,
        ...parsed
      };
      this.updateViewModeFromOptions();
    } catch {
      this.updateViewModeFromOptions();
    }
  }

  private saveViewPreferences(): void {
    try {
      localStorage.setItem(this.viewStorageKey, JSON.stringify(this.viewOptions));
    } catch {
      console.log('Unable to save view preferences.');
    }
  }

  acceptReview(review: ReviewQueueItem): void {
    if (review.isApproval) {
      this.isProcessingReview = true;
      this.rtaApiClient.approveOrderTask(review.orderTaskId).subscribe({
        next: (result) => {
          this.isProcessingReview = false;
          if (result.status) {
            this.notification.showSuccessNotification('Request approved successfully.');
            this.loadReviewQueue();
          } else {
            this.notification.showErrorNotification(result.message || 'Failed to approve request.');
          }
        },
        error: () => {
          this.isProcessingReview = false;
          this.notification.showErrorNotification('Failed to approve request.');
        }
      });
    } else {
      this.openReview(review);
    }
  }

  requestChanges(review: ReviewQueueItem): void {
    this.openReview(review);
  }

  declineReview(review: ReviewQueueItem): void {
    if (review.isApproval) {
      this.rejectingReview = review;
      this.rejectionReason = '';
      this.showRejectDialog = true;
    } else {
      this.openReview(review);
    }
  }

  onConfirmReject(): void {
    if (!this.rejectingReview || !this.rejectionReason.trim()) {
      return;
    }
    this.isProcessingReview = true;
    this.rtaApiClient.rejectOrderTask(this.rejectingReview.orderTaskId, this.rejectionReason.trim()).subscribe({
      next: (result) => {
        this.isProcessingReview = false;
        this.showRejectDialog = false;
        this.rejectingReview = null;
        if (result.status) {
          this.notification.showSuccessNotification('Request rejected.');
          this.loadReviewQueue();
        } else {
          this.notification.showErrorNotification(result.message || 'Failed to reject request.');
        }
      },
      error: () => {
        this.isProcessingReview = false;
        this.notification.showErrorNotification('Failed to reject request.');
      }
    });
  }

  onCancelReject(): void {
    this.showRejectDialog = false;
    this.rejectingReview = null;
    this.rejectionReason = '';
  }

  openReview(review: ReviewQueueItem): void {
    const route = review.isApproval ? '/edit' : '/ejdf';
    const url = this.router.serializeUrl(this.router.createUrlTree([route, review.id]));
    window.open(url, '_blank');
  }

  openUpcomingTask(task: UpcomingTask): void {
    const request = this.allRequests.find(item => item.ID === task.id);
    if (request) {
      this.viewRequest(request);
    } else {
      console.log('Upcoming task not found:', task.id);
    }
  }

  openUpcomingView(): void {
    console.log('Open calendar view');
  }

  openNewRequest(): void {
    this.showNewRequestModal = true;
  }

  closeNewRequestModal(): void {
    this.showNewRequestModal = false;
  }

  selectRequestType(type: 'eRTA' | 'iRTA'): void {
    this.showNewRequestModal = false;
    if (type === 'eRTA') {
      this.router.navigate(['/edit']);
    } else {
      this.router.navigate(['/irta']);
    }
  }

  switchDashboardTab(tab: 'eRTA' | 'iRTA'): void {
    this.dashboardTab = tab;
  }

  onChangeListAction(event: { action: string; change: any }): void {
    if (event.action === 'view' || event.action === 'edit') {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/irta', event.change.id])
      );
      window.open(url, '_blank');
    }
  }

  viewRequest(request: DesignRequest): void {
    // Allow editing if status is "Order Submit" and the user is the submitter
    const isOrderSubmit = request.Status_Name?.toLowerCase() === 'order submit';
    const isSubmitter = request.SubmittedBy === this.currentUser.name;

    if (isOrderSubmit && isSubmitter) {
      // Navigate to edit page for editable requests
      const url = this.router.serializeUrl(this.router.createUrlTree(['/edit', request.ID]));
      window.open(url, '_blank');
    } else {
      // Navigate to EJDF view for other requests
      const url = this.router.serializeUrl(this.router.createUrlTree(['/ejdf', request.ID]));
      window.open(url, '_blank');
    }
  }

  editRequest(request: DesignRequest): void {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/edit', request.ID]));
    window.open(url, '_blank');

  }
  onGridSearch(searchTerm: string): void {
    this.gridSearchTerm = searchTerm;
    this.filterForm.get('search')?.setValue(searchTerm);
    this.onFilterChange();
  }

  canEditRequest(request: DesignRequest): boolean {
    const isOrderSubmit = request.Status_Name?.toLowerCase() === 'order submit';
    const isSubmitter = request.SubmittedBy === this.currentUser.name;
    return isOrderSubmit && isSubmitter;
  }

  exportData(): void {
    console.log('Export data');
  }

  /**
   * Parse task data from JSON string
   */
  parseTaskData(taskDataJson: string | undefined): ParentTask[] {
    if (!taskDataJson) return [];
    try {
      const parsed = JSON.parse(taskDataJson);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error('Error parsing TaskData:', e);
      return [];
    }
  }

  /**
   * Parse child tasks from JSON string
   */
  parseChildTasks(childTasksJson: string): TaskItem[] {
    if (!childTasksJson) return [];
    try {
      const parsed = JSON.parse(childTasksJson);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error('Error parsing childTasks:', e);
      return [];
    }
  }

  /**
   * Get the CSS class for a task status
   */
  getTaskStatusClass(status: string): string {
    const statusMap: Record<string, string> = {
      'C': 'task-completed',
      'Q': 'task-queued',
      'Pr': 'task-inprogress',
      'Pe': 'task-pending'
    };
    return statusMap[status] || 'task-pending';
  }

  /**
   * Get status abbreviation display
   */
  getStatusAbbreviation(status: string): string {
    const abbrevMap: Record<string, string> = {
      'C': 'C',
      'Q': 'Q',
      'Pr': 'Pr',
      'Pe': 'Pe'
    };
    return abbrevMap[status] || status;
  }

  /**
   * Get the number of child tasks for a parent task (for flex sizing)
   * Returns at least 1 to ensure parent tasks without children still display
   */
  getChildTaskCount(parentTask: ParentTask): number {
    const childTasks = this.parseChildTasks(parentTask.childTasks);
    console.log('Parent:', parentTask.Name, '| Child count:', childTasks.length, '| Children:', childTasks);
    return Math.max(childTasks.length, 1);
  }

  /**
   * Calculate the exact width for a parent task to encompass its children
   * Width = (childCount * childWidth) + ((childCount - 1) * gap)
   * childWidth = 24px, gap = 4px
   */
  getParentTaskWidth(parentTask: ParentTask): string {
    const childCount = this.getChildTaskCount(parentTask);
    const childWidth = 24; // px
    const gap = 4; // px
    const width = (childCount * childWidth) + ((childCount - 1) * gap);
    return width + 'px';
  }

  openActionsMenu(request: DesignRequest, event: Event): void {
    console.log('Open actions menu for:', request);
  }

  ngOnDestroy(): void {
    // Disconnect SignalR when component is destroyed
    this.signalRService.stopConnection();
  }
}
